import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { AccountResponse, SiteResponse } from "api/ApiClient";
import { BASE_API_URL } from "store/Me";
import { UseLivlyLeaseUser } from "shared/api/LivlyUserProvider";
import { isSuperUser } from "shared/api/isSuperUser";
import { SITES_VIEW, setSitesViewPreference } from "shared/utils/sitesViewPreference";
import useDebounce from "utils/useDebounce";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { EllipsisVertical, Filter, Search } from "lucide-react";
import { AdminTable } from "components/shared/AdminTable";
import { AdminTablePagination } from "components/shared/AdminTablePagination";
import type { SiteStatus } from "./SiteStatusModal";
import { SITE_STATUS_OPTIONS as STATUS_OPTIONS } from "./SiteStatusModal";
import { statusBadgeVariant } from "shared/utils/statusChipColor";
import { SITES_QUERY_KEY } from "./siteQueryKeys";

function AccountInfoDialog({
  account,
  onClose,
}: {
  account: AccountResponse | null;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={!!account}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <DialogContent aria-describedby={undefined} className="sm:max-w-xs">
        <DialogHeader>
          <DialogTitle>Account Info</DialogTitle>
        </DialogHeader>
        {account && (
          <div className="flex flex-col gap-2.5">
            <div>
              <div className="text-[11px] text-muted-foreground uppercase">Name</div>
              <div>{account.name ?? "—"}</div>
            </div>
            <div>
              <div className="text-[11px] text-muted-foreground uppercase">System Type</div>
              <div>{account.systemType ?? "—"}</div>
            </div>
            <div>
              <div className="text-[11px] text-muted-foreground uppercase">Account ID</div>
              <div style={{ fontFamily: "monospace" }}>{account.id ?? "—"}</div>
            </div>
            <div>
              <div className="text-[11px] text-muted-foreground uppercase">Owner Tenant</div>
              <div>
                {account.ownerTenant?.name ?? account.ownerTenant?.externalId ?? "—"}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// PropertyExternalId arrives as a semicolon-joined string of all property IDs
interface SiteListResponse extends SiteResponse {
  propertyExternalId?: string;
  hasPendingChanges?: boolean;
  status?: SiteStatus | null;
}

const DEFAULT_STATUS_FILTER: SiteStatus[] = ["Staging", "Active", "Degraded"];
const DEFAULT_SITE_STATUS: SiteStatus = "Staging";
const DEFAULT_PAGE_SIZE = 50;

function checksumNeedsSync(site: SiteListResponse): boolean {
  return !!site.hasPendingChanges;
}

function PropertyCell({ value }: { value?: string }) {
  if (!value) return <span>—</span>;
  const parts = value.split(";").map((s) => s.trim()).filter(Boolean);
  if (parts.length === 0) return <span>—</span>;
  if (parts.length === 1) return <span>{parts[0]}</span>;
  return (
    <span>
      {parts[0]}
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="ml-1.5 cursor-default rounded-[3px] border px-1 py-px text-[11px] text-muted-foreground"
            tabIndex={0}
          >
            +{parts.length - 1}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">{parts.slice(1).join(", ")}</TooltipContent>
      </Tooltip>
    </span>
  );
}

function SiteStatusIndicator({ status }: { status?: SiteStatus | null }) {
  const normalizedStatus = status ?? DEFAULT_SITE_STATUS;
  const statusColor =
    normalizedStatus === "Active"
      ? "bg-green-500"
      : normalizedStatus === "Degraded"
        ? "bg-red-500"
        : normalizedStatus === "Staging"
          ? "bg-orange-500"
          : "bg-slate-400";

  return (
    <span className="inline-flex items-center gap-2 font-medium whitespace-nowrap text-slate-700">
      <span
        aria-hidden="true"
        className={`h-1.5 w-1.5 rounded-full ${statusColor}`}
      />
      {normalizedStatus}
    </span>
  );
}

function SiteRowActions({ siteId }: { siteId: string }) {
  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <button
              aria-label="Site actions"
              className="inline-flex items-center justify-center rounded-md size-8 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              type="button"
            >
              <EllipsisVertical className="size-4" />
            </button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>Site actions</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <Link to={`/admin/sites/${siteId}`}>View site details</Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

async function getSitesList(
  search: string,
  page: number,
  pageSize: number,
  statuses: SiteStatus[],
): Promise<SiteListResponse[]> {
  const urlParams = new URLSearchParams();
  urlParams.append("searchString", search);
  urlParams.append("page", page.toString());
  urlParams.append("pageSize", pageSize.toString());
  statuses.forEach((status) => urlParams.append("statuses", status));
  const response = await axios.get<SiteListResponse[]>(
    `${BASE_API_URL}/api/Sites?${urlParams.toString()}`,
  );
  return response.data;
}

function useGetSitesList(search: string, page: number, pageSize: number, statuses: SiteStatus[]) {
  return useQuery({
    queryKey: [SITES_QUERY_KEY, search, page, pageSize, statuses],
    queryFn: () => getSitesList(search, page, pageSize, statuses),
  });
}

export default function AdminSites() {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<SiteStatus[]>(DEFAULT_STATUS_FILTER);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const debouncedSearch = useDebounce(search, 500);
  const user = UseLivlyLeaseUser();
  const canToggle = isSuperUser(user);

  const { data, isError, isLoading } = useGetSitesList(
    debouncedSearch,
    page,
    pageSize,
    statusFilter,
  );
  const [selectedAccount, setSelectedAccount] = useState<AccountResponse | null>(null);

  const sites = data ?? [];
  const activeFilterCount = statusFilter.length;

  const toggleStatusFilter = (status: SiteStatus) => {
    setStatusFilter((currentFilters) => {
      if (currentFilters.includes(status)) {
        return currentFilters.length === 1
          ? currentFilters
          : currentFilters.filter((currentStatus) => currentStatus !== status);
      }

      return [...currentFilters, status];
    });
    setPage(0);
  };

  return (
    <main className="space-y-6">
      <header className="pb-5 border-b border-slate-200 sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold leading-7 text-slate-900">
            Sites
          </h1>
          {canToggle && (
            <Link
              to="/admin/sites-legacy"
              onClick={() => setSitesViewPreference(SITES_VIEW.LEGACY)}
              className="inline-block mt-1 text-sm underline text-slate-500 underline-offset-2 hover:text-slate-700"
            >
              Switch to legacy view
            </Link>
          )}
        </div>
      </header>

      <AdminTable
        aria-labelledby="sites-list-heading"
        toolbar={
          <div className="flex flex-col gap-4 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 id="sites-list-heading" className="text-base font-semibold text-slate-900">
              Sites
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {isLoading ? "Loading sites" : `${sites.length} sites on this page`}
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <label className="relative block w-full sm:w-72">
              <span className="sr-only">Search sites</span>
              <Search
                aria-hidden="true"
                className="absolute -translate-y-1/2 pointer-events-none left-3 top-1/2 size-4 text-slate-400"
              />
              <input
                className="w-full h-10 py-2 pr-3 text-sm transition-colors bg-white border rounded-md outline-none border-slate-200 pl-9 text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                placeholder="Search sites"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
              />
            </label>
            <DropdownMenu onOpenChange={setFilterMenuOpen} open={filterMenuOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center justify-center h-10 gap-2 px-3 text-sm font-medium transition-colors bg-white border rounded-md border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                >
                  <Filter className="size-4" />
                  Filter{activeFilterCount === STATUS_OPTIONS.length ? "" : ` (${activeFilterCount})`}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Filter by status</DropdownMenuLabel>
                {STATUS_OPTIONS.map((status) => {
                  const isSelected = statusFilter.includes(status);
                  const isOnlySelected = isSelected && statusFilter.length === 1;

                  return (
                    <DropdownMenuCheckboxItem
                      key={status}
                      checked={isSelected}
                      disabled={isOnlySelected}
                      onSelect={(event) => {
                        event.preventDefault();
                        toggleStatusFilter(status);
                      }}
                    >
                      {status}
                    </DropdownMenuCheckboxItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          </div>
        }
        footer={
          <AdminTablePagination
            currentPage={page}
            pageSize={pageSize}
            visibleRowCount={sites.length}
            isLoading={isLoading}
            disableNext={sites.length < pageSize}
            onNext={() => setPage((currentPage) => currentPage + 1)}
            onPrevious={() => setPage((currentPage) => Math.max(0, currentPage - 1))}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
          />
        }
      >

        {isLoading ? (
          <tbody>
            <tr>
              <td colSpan={7}>
                <div className="flex items-center justify-center gap-3 text-sm min-h-80 text-slate-500">
                  Loading sites
                </div>
              </td>
            </tr>
          </tbody>
        ) : isError ? (
          <tbody>
            <tr>
              <td colSpan={7}>
                <div className="flex items-center justify-center px-4 text-sm text-center min-h-80 text-rose-700">
                  Unable to load sites. Please try again.
                </div>
              </td>
            </tr>
          </tbody>
        ) : (
          <>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Status</th>
                <th scope="col">System Site ID</th>
                <th scope="col">Account</th>
                <th scope="col">Properties</th>
                <th scope="col">Time Zone</th>
                <th scope="col" className="text-right w-14">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sites.map((site) => (
                <tr key={site.id}>
                  <td>
                    <div className="flex items-center gap-2 min-w-44">
                      <Link to={`/admin/sites/${site.id}`}>
                        {site.name}
                      </Link>
                      {checksumNeedsSync(site) && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-800"
                              tabIndex={0}
                            >
                              Sync needed
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            Site has pending changes; sync needed
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                  <td>
                    <SiteStatusIndicator status={site.status} />
                  </td>
                  <td className="whitespace-nowrap text-slate-500">
                    {site.systemSiteId ?? "—"}
                  </td>
                  <td>
                    {site.account ? (
                      <button
                        type="button"
                        className="font-medium text-left text-slate-700 hover:text-blue-700 hover:underline"
                        onClick={() => setSelectedAccount(site.account!)}
                      >
                        {site.account.name ?? "—"}
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <PropertyCell value={site.propertyExternalId} />
                  </td>
                  <td className="whitespace-nowrap text-slate-500">
                    {site.timeZone ?? "—"}
                  </td>
                  <td className="text-right">
                    {site.id ? <SiteRowActions siteId={site.id} /> : null}
                  </td>
                </tr>
              ))}
              {sites.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-16 text-sm text-center text-slate-500">
                    No sites found.
                  </td>
                </tr>
              )}
            </tbody>
          </>
        )}
      </AdminTable>
      <AccountInfoDialog
        account={selectedAccount}
        onClose={() => setSelectedAccount(null)}
      />
    </main>
  );
}
